import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ClipboardCheck, UserCheck, UserX, Clock, AlertCircle } from 'lucide-react';

const AttendanceDialog = ({
  showAttendanceDialog,
  setShowAttendanceDialog,
  attendanceData,
  attendanceLoading,
  updateAttendance,
  translate,
}) => {
  return (
    <Dialog open={!!showAttendanceDialog} onOpenChange={(open) => !open && setShowAttendanceDialog(null)}>
      <DialogContent className="bg-gradient-to-br from-blue-900 to-indigo-900 border-blue-700 text-white max-w-lg" data-testid="attendance-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-200 flex items-center">
            <ClipboardCheck className="mr-2" size={20} />
            {translate('attendance', 'Attendance')}
            {attendanceData.session_name && (
              <span className="ml-2 text-sm font-normal text-blue-400">- {attendanceData.session_name}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {attendanceLoading ? (
          <div className="text-center py-8 text-blue-300">
            <span className="animate-spin inline-block mr-2">⏳</span>
            {translate('loading', 'Loading...')}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Attendance Stats */}
            {attendanceData.stats && (
              <div className="grid grid-cols-5 gap-2 text-center text-xs">
                <div className="p-2 bg-green-700/30 rounded">
                  <div className="text-lg font-bold text-green-300">{attendanceData.stats.present}</div>
                  <div className="text-green-400">{translate('present', 'Present')}</div>
                </div>
                <div className="p-2 bg-red-700/30 rounded">
                  <div className="text-lg font-bold text-red-300">{attendanceData.stats.absent}</div>
                  <div className="text-red-400">{translate('absent', 'Absent')}</div>
                </div>
                <div className="p-2 bg-yellow-700/30 rounded">
                  <div className="text-lg font-bold text-yellow-300">{attendanceData.stats.excused}</div>
                  <div className="text-yellow-400">{translate('excused', 'Excused')}</div>
                </div>
                <div className="p-2 bg-orange-700/30 rounded">
                  <div className="text-lg font-bold text-orange-300">{attendanceData.stats.late}</div>
                  <div className="text-orange-400">{translate('late', 'Late')}</div>
                </div>
                <div className="p-2 bg-slate-700/30 rounded">
                  <div className="text-lg font-bold text-slate-300">{attendanceData.stats.pending}</div>
                  <div className="text-slate-400">{translate('pending', 'Pending')}</div>
                </div>
              </div>
            )}

            {/* Participant List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {attendanceData.attendance?.length === 0 ? (
                <p className="text-center text-blue-400 py-4">{translate('noParticipants', 'No participants assigned')}</p>
              ) : (
                attendanceData.attendance?.map((participant) => (
                  <div
                    key={participant.freediver_id}
                    className="flex items-center justify-between p-3 bg-blue-800/30 rounded-lg border border-blue-700/30"
                  >
                    <div className="flex items-center gap-3">
                      {participant.photo_url ? (
                        <img src={participant.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
                          {participant.first_name?.[0]}{participant.last_name?.[0]}
                        </div>
                      )}
                      <span className="text-white">{participant.first_name} {participant.last_name}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant={participant.status === 'present' ? 'default' : 'ghost'}
                        className={`h-8 w-8 p-0 ${participant.status === 'present' ? 'bg-green-600' : 'text-green-400 hover:bg-green-700/50'}`}
                        onClick={() => updateAttendance(showAttendanceDialog, participant.freediver_id, 'present')}
                        title={translate('present', 'Present')}
                        data-testid={`attendance-present-${participant.freediver_id}`}
                      >
                        <UserCheck size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant={participant.status === 'absent' ? 'default' : 'ghost'}
                        className={`h-8 w-8 p-0 ${participant.status === 'absent' ? 'bg-red-600' : 'text-red-400 hover:bg-red-700/50'}`}
                        onClick={() => updateAttendance(showAttendanceDialog, participant.freediver_id, 'absent')}
                        title={translate('absent', 'Absent')}
                        data-testid={`attendance-absent-${participant.freediver_id}`}
                      >
                        <UserX size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant={participant.status === 'excused' ? 'default' : 'ghost'}
                        className={`h-8 w-8 p-0 ${participant.status === 'excused' ? 'bg-yellow-600' : 'text-yellow-400 hover:bg-yellow-700/50'}`}
                        onClick={() => updateAttendance(showAttendanceDialog, participant.freediver_id, 'excused')}
                        title={translate('excused', 'Excused')}
                        data-testid={`attendance-excused-${participant.freediver_id}`}
                      >
                        <AlertCircle size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant={participant.status === 'late' ? 'default' : 'ghost'}
                        className={`h-8 w-8 p-0 ${participant.status === 'late' ? 'bg-orange-600' : 'text-orange-400 hover:bg-orange-700/50'}`}
                        onClick={() => updateAttendance(showAttendanceDialog, participant.freediver_id, 'late')}
                        title={translate('late', 'Late')}
                        data-testid={`attendance-late-${participant.freediver_id}`}
                      >
                        <Clock size={16} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceDialog;
